import {differenceInCalendarDays} from 'date-fns'
import {CalendarStore, Shift} from '@components/calendar/calendar-store'
import {DraggableStore} from '../draggable/draggable-store'
import {taskHeight, TasksAllDay} from './all-day-tasks'

export class TaskTopPlacement {
  usedSlots = new Map<number, Set<number>>() // day -> Set(index)
  height = 0

  calculate(allTasks: TasksAllDay[], day: number): void {
    let index = 0
    const used = this.usedSlots.get(day) ?? new Set()
    for (const task of allTasks) {
      while (used.has(index)) {
        index++
      }
      task.top = index * taskHeight
      this.height = Math.max(this.height, task.top + taskHeight)
      if (task.duration > 1) {
        for (let i = day; i < 7 && i < day + task.duration; i++) {
          const daySlots = this.usedSlots.get(i) ?? new Set()
          daySlots.add(index)
          this.usedSlots.set(i, daySlots)
        }
      }
      index += 1
    }
  }
}

export const getSingleDayTasks = (
  store: CalendarStore,
  shift: Shift
): TasksAllDay[] => {
  return store.getTasksWithoutStartDateWithoutTime(shift).map(task => ({
    ...task,
    duration: 1,
    allDayType: 'single-day',
    id: task.task.id
  }))
}

export const getMultipleDaysTasks = (
  store: CalendarStore,
  shift: Shift
): TasksAllDay[] => {
  return store
    .getTasksWithStartDateWithoutTime(shift)
    .sort((a, b) => {
      if (
        differenceInCalendarDays(a.roundedDeadline, a.roundedStartDate) >
        differenceInCalendarDays(b.roundedDeadline, b.roundedStartDate)
      ) {
        return -1
      }
      if (
        differenceInCalendarDays(a.roundedDeadline, a.roundedStartDate) <
        differenceInCalendarDays(b.roundedDeadline, b.roundedStartDate)
      ) {
        return 1
      }
      return 0
    })
    .map(task => ({
      ...task,
      duration:
        differenceInCalendarDays(task.roundedDeadline, task.roundedStartDate) +
        1,
      allDayType: 'multiple-days',
      id: task.task.id
    }))
}

export const getLongWithTimeTasks = (
  store: CalendarStore,
  shift: Shift
): TasksAllDay[] => {
  return store
    .getTasksWithStartDate(shift)
    .sort((a, b) => {
      if (
        differenceInCalendarDays(a.roundedDeadline, a.roundedStartDate) >
        differenceInCalendarDays(b.roundedDeadline, b.roundedStartDate)
      ) {
        return -1
      }
      if (
        differenceInCalendarDays(a.roundedDeadline, a.roundedStartDate) <
        differenceInCalendarDays(b.roundedDeadline, b.roundedStartDate)
      ) {
        return 1
      }
      return 0
    })
    .map(task => ({
      ...task,
      duration:
        differenceInCalendarDays(task.roundedDeadline, task.roundedStartDate) -
        1,
      allDayType: 'long-with-time',
      id: task.task.id
    }))
}

const maxNumberOfTasks = 14
export const minNumberOfTasks = 4

export const getMaxHeight = (showMoreAllDayTasks: boolean): number => {
  return showMoreAllDayTasks
    ? maxNumberOfTasks * taskHeight
    : minNumberOfTasks * taskHeight
}

export const getZIndex = (
  draggableState: DraggableStore['state'],
  task: TasksAllDay
): number | undefined => {
  return draggableState.draggedItem.id &&
    draggableState.draggedItem.id === task.task.id &&
    draggableState.draggedItem.changeAction === 'change-deadline'
    ? 20
    : undefined
}

export const getPointerEventsNone = (
  draggableState: DraggableStore['state'],
  task: TasksAllDay
) => {
  return (
    draggableState.draggedItem.id &&
    draggableState.draggedItem.id !== task.task.id &&
    'pointer-events-none'
  )
}
